import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { StoreConfig, StoreConfigService } from '@scalefast/config-angular';
import {
  CATEGORY_FILTERS_OPTIONS,
  CartProduct,
  CategoryPageFilters,
  Metaproduct,
  Product,
  UpdateWishlistResponse,
  Wishlist,
  createCartProductFromProduct,
} from '@scalefast/core';
import {
  AddToCartService,
  CartService,
  CrossSellProductsService,
  LoginService,
  ProductService,
  SearchResultsPageService,
  WishlistService,
} from '@scalefast/ecommerce-core';
import { CategoryPagePaginationFiltersAdapter } from '@scalefast/sam';
import { EMPTY, Subscription, map, switchMap, take } from 'rxjs';
import { AppStoreProductListComponent } from '../../pages/product/product-list/product-list.component';

export abstract class AppProductListPageComponent {
  protected filterService = inject(SearchResultsPageService);
  protected route = inject(ActivatedRoute);
  protected loginService = inject(LoginService);
  protected cartService = inject(CartService);
  protected productService = inject(ProductService);
  protected wishlistService = inject(WishlistService);
  protected storeConfigService = inject(StoreConfigService);
  protected crossSellProductsService = inject(CrossSellProductsService);
  protected addToCartService = inject(AddToCartService);
  protected destroyRef = inject(DestroyRef);

  public currentOffset: number = CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_OFFSET;
  public pageSize: number = CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_SIZE;
  public currentPage: number = 1;
  public showNextPage: boolean = false;
  public showLoadMore: boolean = false;

  protected loginURL: string = '';
  protected isLogged: boolean = false;
  protected isLoadMore: boolean = false;
  protected showQuantityErrorModal = false;
  protected totalProducts: number = 0;
  protected productRequested: Product | undefined;
  protected wishlist: Wishlist | undefined;
  protected isInWishlist = false;
  protected filtersSubscription: Subscription | undefined;
  protected crossSellProducts: Product[] = [];
  protected storeConfig: StoreConfig;
  protected metaproductsRequested: Metaproduct[] | undefined;

  MAX_ITEM_QUANTITY: number;

  storeProductListComponent: AppStoreProductListComponent | undefined;

  constructor() {
    this.storeConfig = this.storeConfigService.get();
    this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;

    this.loginService.isLogged().subscribe((isLogged: boolean) => (this.isLogged = isLogged));
  }

  public abstract getFilteredProducts(feedId?: number, filters?: CategoryPageFilters): void;

  hasNextPage(totalItems: number) {
    if (totalItems <= this.currentOffset + this.pageSize) {
      this.showNextPage = false;
      return;
    }
    this.showNextPage = true;
  }

  hasLoadMore(totalItems: number) {
    if (totalItems <= this.currentOffset + this.pageSize) {
      this.showLoadMore = false;
      return;
    }
    this.showLoadMore = true;
  }

  previousPageEventHandler() {
    this.currentOffset = this.currentOffset - this.pageSize;
    const filters = CategoryPagePaginationFiltersAdapter.buildPartialFiltersFromPagination(this.currentOffset);
    this.filterService.setFilters(filters);
  }

  nextPageEventHandler() {
    this.currentPage = this.currentPage + 1;
    this.currentOffset = this.currentOffset + this.pageSize;
    const filters = CategoryPagePaginationFiltersAdapter.buildPartialFiltersFromPagination(this.currentOffset);
    this.filterService.setFilters(filters);
  }

  loadMoreEventHandler() {
    this.isLoadMore = true;
    this.nextPageEventHandler();
  }

  onGetProductById(productId: string): void {
    this.productService
      .getProduct$(parseInt(productId))
      .pipe(
        map((data: Product) => {
          this.productRequested = data;
          this.getCrossSellProducts(this.productRequested);
          if (this.wishlist) {
            this.isInWishlist = this.wishlist.products.some((product) => productId == product.productID);
          }
        }),
      )
      .subscribe();
  }

  setWishlistedProductEventHandler(product: Product) {
    this.isInWishlist = false;
    this.productRequested = product;

    if (this.wishlist) {
      this.wishlist.products.map((product: any) => {
        if (this.productRequested?.id === product.productID) {
          this.isInWishlist = true;
        }
      });
    }
  }

  getWishlist(): void {
    const wishlist$ = this.wishlistService.getWishlist$();

    wishlist$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((wishlist) => {
      this.wishlist = wishlist;
    });
  }

  addToWishlistEventHandler(): void {
    if (this.wishlist && this.productRequested) {
      this.wishlistService
        .addIntoWishlist$(this.wishlist, this.productRequested)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdateWishlistResponse) => {
          if (response.response_data) {
            this.isInWishlist = true;
            this.getWishlist();
          }
        });
    }
  }

  removeFromWishlistEventHandler(): void {
    if (this.wishlist && this.productRequested) {
      this.wishlistService
        .removeFromWishlist$(this.wishlist, this.productRequested)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdateWishlistResponse) => {
          if (response.response_data) {
            this.isInWishlist = false;
            this.getWishlist();
          }
        });
    }
  }

  checkLoggedUserEventHandler(): void {
    this.wishlistService.checkLoggedUser();
  }

  getCrossSellProducts(product: Product): void {
    this.crossSellProductsService
      .getCrossSellProducts(product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((crossSellProducts) => {
        this.crossSellProducts = crossSellProducts;
      });
  }

  getMetaproducts(productID: string): void {
    this.productService
      .getProduct$(parseInt(productID))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((product: Product) => {
        this.metaproductsRequested = product.metaproductsV4;
      });
  }
}
